import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useParams, Route, Routes } from 'react-router-dom';
import { Box, Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';

import { Breadcrumbs, BurgerMenu, Header } from '../../components';
import { AUTH_QUERY, GET_MY_SITE_QUERY } from '../../gql';
import { SiteActionBar } from './SiteActionBar';
import { SiteMenu } from './SiteMenu';
import { SiteSettings } from './SiteSettings';
import { SiteFinds } from './SiteFinds';
import { SitePas } from './SitePas';
import { SiteImages } from './SiteImages';
import { SiteObjects } from './SiteObjects';
import { SiteAccess } from './SiteAccess';
import { SiteSync } from './SiteSync';
import { FindSettings } from '../../components/FindSettings';
import { useIntl } from 'react-intl';

const pageStyles = {
  width: '100%',
  minHeight: '100%',
};
const containerStyles = {
  pb: 6,
};
const layoutStyles = {
  alignItems: 'flex-start',
};
const contentStyles = {
  flexGrow: 1,
  gap: 2,
  minWidth: 0,
};
const topBarStyles = {
  minHeight: 40,
};

const Site = React.memo(() => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [findListMode, setFindListMode] = useState<'grid' | 'gallery'>('grid');

  const { siteId } = useParams();
  const { called, data, loading } = useQuery(GET_MY_SITE_QUERY, {
    variables: { siteId },
    fetchPolicy: 'cache-first',
    skip: !siteId,
  });
  const { data: authData } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const site = data?.getMySite;
  const canUsePremium =
    authData?.viewer?.user?.role === 'admin' ||
    authData?.viewer?.premiumAccess?.isPremium === true;
  const showSync = Boolean(site && !site.shared && canUsePremium);
  const pasEnabled = Boolean(site?.amcrProjectIds?.length);

  return (
    <Box sx={pageStyles}>
      <Helmet
        title={intl.formatMessage({
          id: 'site.pageTitle',
          defaultMessage: 'Site | Acolyte',
        })}
      />
      <Header
        mainIcon={
          isMobile && <BurgerMenu menu={<SiteMenu showSync={showSync} />} />
        }
      />
      <Container maxWidth="xl" sx={containerStyles}>
        <Stack direction="row" sx={layoutStyles}>
          {!isMobile && <SiteMenu showSync={showSync} />}
          <Stack sx={contentStyles}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
              sx={topBarStyles}
            >
              <Breadcrumbs />
              <SiteActionBar
                findListMode={findListMode}
                setFindListMode={setFindListMode}
                site={site}
              />
            </Stack>
            <Routes>
              <Route
                index
                element={
                  <SiteSettings
                    siteId={siteId}
                    called={called}
                    loading={loading}
                    site={site}
                  />
                }
              />
              <Route
                path="finds/create"
                element={<FindSettings forSite={siteId} />}
              />
              <Route
                path="finds"
                element={<SiteFinds findListMode={findListMode} />}
              />
              <Route
                path="images"
                element={
                  <SiteImages
                    siteId={siteId}
                    called={called}
                    loading={loading}
                    site={site}
                  />
                }
              />
              <Route
                path="pas"
                element={<SitePas pasEnabled={pasEnabled} />}
              />
              <Route
                path="pas/:stage"
                element={<SitePas pasEnabled={pasEnabled} />}
              />
              <Route path="objects" element={<SiteObjects />} />
              <Route path="access" element={<SiteAccess />} />
              <Route path="sync" element={<SiteSync siteId={siteId} />} />
            </Routes>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
});

export { Site };
